import { useMemo } from "react";
import { trpc } from "@/lib/trpc";

export type ResumoDeMaterial = {
  serviceId: string;
  aplicada: boolean;
  linhas: number;
  emAberto: number;
  semPreco: number;
  totalCents: number | null;
};

/**
 * Pendência de material de todos os serviços em aberto, numa consulta só.
 *
 * Existe porque concluir um serviço **não** é bloqueado por material faltando — dado
 * administrativo não pode travar quem está na bancada às 18h. O preço dessa escolha é que a
 * pendência precisa ser visível em todo lugar onde alguém decide, senão vira dívida invisível.
 */
export function useResumoDeMaterial() {
  const query = trpc.consumo.resumoDaProducao.useQuery(undefined, {
    // Muda com o trabalho de todo mundo no laboratório, não só com o meu.
    staleTime: 30_000,
  });
  const porServico = useMemo(() => {
    const mapa = new Map<string, ResumoDeMaterial>();
    for (const r of (query.data ?? []) as ResumoDeMaterial[]) mapa.set(r.serviceId, r);
    return mapa;
  }, [query.data]);
  return { porServico, carregando: query.isLoading };
}

/**
 * O motivo da pendência, já em texto — ou null quando não há nada pendente.
 *
 * `incluirNaoRegistrado` separa dois casos que parecem um só. Serviço **sem nada registrado** é o
 * estado inicial de todo serviço, inclusive dos milhares que existiam antes desta tela: avisar
 * sobre ele numa lista pinta a planilha inteira de âmbar e o aviso deixa de significar qualquer
 * coisa. Já no momento de concluir, avisar é exatamente o que serve — é a última chance de
 * registrar, e é uma decisão só.
 *
 * Serviço **começado e deixado pela metade** é pendência de verdade em qualquer lugar.
 */
export function motivoDaPendencia(
  r: ResumoDeMaterial | undefined,
  { incluirNaoRegistrado = true }: { incluirNaoRegistrado?: boolean } = {},
): string | null {
  if (!r) return null;
  if (!r.aplicada || r.linhas === 0) {
    return incluirNaoRegistrado ? "Nenhuma ficha técnica aplicada" : null;
  }
  if (r.emAberto > 0) {
    return r.emAberto === 1 ? "1 insumo sem escolha" : `${r.emAberto} insumos sem escolha`;
  }
  if (r.semPreco > 0) {
    return r.semPreco === 1 ? "1 insumo sem preço de compra" : `${r.semPreco} insumos sem preço de compra`;
  }
  return null;
}

/** A pendência de UM serviço, para o badge da aba e o aviso ao concluir. */
export function usePendenciaDeMaterial(serviceId: string | null | undefined) {
  const { porServico } = useResumoDeMaterial();
  const resumo = serviceId ? porServico.get(serviceId) : undefined;
  return { resumo, motivo: motivoDaPendencia(resumo) };
}
